import React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Microphone, Stop, Keyboard, Notebook, ClipboardText, Eye, FileText, CheckCircle,
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const DESTINATIONS = [
  { id: 'journal', label: 'Journal', desc: 'Note dans le journal de chantier', icon: Notebook, color: 'bg-violet-600', border: 'border-violet-200', bg: 'bg-violet-50', text: 'text-violet-700' },
  { id: 'reserve', label: 'Réserve', desc: 'Créer une réserve / non-conformité', icon: ClipboardText, color: 'bg-emerald-600', border: 'border-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  { id: 'observation', label: 'Observation', desc: 'Observation sécurité sur chantier', icon: Eye, color: 'bg-red-500', border: 'border-red-200', bg: 'bg-red-50', text: 'text-red-700' },
  { id: 'compte_rendu', label: 'Compte-rendu', desc: 'Ajouter au prochain rapport', icon: FileText, color: 'bg-blue-600', border: 'border-blue-200', bg: 'bg-blue-50', text: 'text-blue-700' },
];

export function QuickVoiceMode({
  chantierId, onChantierChange, chantiers,
  isRecording, isProcessing, transcription,
  onStartRecording, onStopRecording, onCancel, onSwitchToText,
  onSaveToDestination, isSaving,
}) {
  return (
    <div className="py-4 space-y-4 text-center">
      {/* Chantier selector - always visible */}
      <div className="text-left">
        <Label>Chantier (optionnel)</Label>
        <Select value={chantierId} onValueChange={onChantierChange}>
          <SelectTrigger className="mt-1 h-12 text-base" data-testid="quick-voice-chantier">
            <SelectValue placeholder="Sélectionner un chantier" />
          </SelectTrigger>
          <SelectContent position="popper" className="z-[100]">
            {chantiers.map(c => (
              <SelectItem key={c.id} value={c.id} className="py-3">{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* STEP 1: Recording */}
      {!isProcessing && !transcription && (
        <div className="flex flex-col items-center gap-3 py-2">
          <button
            onClick={isRecording ? onStopRecording : onStartRecording}
            className={`w-20 h-20 rounded-full flex items-center justify-center transition-all
              ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-[#FF6B00] hover:bg-orange-600'}`}
            data-testid="quick-voice-record"
          >
            {isRecording ? (
              <Stop size={36} className="text-white" weight="fill" />
            ) : (
              <Microphone size={36} className="text-white" weight="fill" />
            )}
          </button>
          <p className="text-base font-medium text-[#111827]">
            {isRecording ? 'Appuyez pour arreter' : 'Appuyez pour enregistrer'}
          </p>
          {isRecording && (
            <div className="flex items-center gap-2 text-red-500 text-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
              Enregistrement en cours...
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onCancel} className="h-10 px-6 text-sm">Annuler</Button>
          </div>
          {!isRecording && onSwitchToText && (
            <button onClick={onSwitchToText}
              className="flex items-center justify-center gap-2 py-2 text-sm font-medium text-[#4B5563] hover:text-[#111827] hover:bg-gray-100 rounded-lg transition-colors w-full"
              data-testid="switch-to-text">
              <Keyboard size={16} weight="bold" /> Saisir au clavier
            </button>
          )}
        </div>
      )}

      {/* STEP 2: Processing */}
      {isProcessing && (
        <div className="py-6">
          <Loader2 className="w-10 h-10 animate-spin text-[#FF6B00] mx-auto mb-3" />
          <p className="text-[#4B5563] text-sm">Transcription en cours...</p>
        </div>
      )}

      {/* STEP 3: Transcription result + destination choice */}
      {transcription && !isProcessing && (
        <div className="space-y-4 text-left">
          {/* Transcription display */}
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-xl" data-testid="voice-transcription-result">
            <div className="flex items-center gap-2 mb-1.5">
              <CheckCircle size={16} className="text-emerald-600" weight="fill" />
              <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide">Transcription reussie</span>
            </div>
            <p className="text-sm text-[#111827] leading-relaxed">{transcription}</p>
          </div>

          {/* Destination selector */}
          <div>
            <p className="text-sm font-semibold text-[#111827] mb-2">Ou enregistrer cette note ?</p>
            <div className="grid grid-cols-2 gap-2">
              {DESTINATIONS.map(dest => {
                const Icon = dest.icon;
                return (
                  <button key={dest.id}
                    onClick={() => onSaveToDestination(dest.id)}
                    disabled={isSaving}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border-2 ${dest.border} ${dest.bg} hover:shadow-md active:scale-[0.97] transition-all text-left`}
                    data-testid={`dest-${dest.id}`}>
                    <div className={`w-9 h-9 rounded-lg ${dest.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={18} className="text-white" weight="bold" />
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-bold ${dest.text}`}>{dest.label}</p>
                      <p className="text-[10px] text-gray-500 leading-tight">{dest.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {isSaving && (
            <div className="flex items-center justify-center gap-2 py-2 text-sm text-[#4B5563]">
              <Loader2 className="h-4 w-4 animate-spin" /> Enregistrement...
            </div>
          )}

          <Button variant="outline" onClick={onCancel} className="w-full h-10 text-sm">Annuler</Button>
        </div>
      )}
    </div>
  );
}
